import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, RefreshCw, Trash2, Mail, Phone, Building2, LogOut, Inbox, Briefcase } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  neu: "bg-blue-100 text-blue-700",
  bearbeitet: "bg-green-100 text-green-700",
  archiviert: "bg-slate-100 text-slate-600",
};

const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem("mag_admin_token") || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (tk) => {
    setLoading(true);
    setError("");
    try {
      const [c, a] = await Promise.all([
        axios.get(`${API}/contact`, { headers: { "X-Admin-Token": tk } }),
        axios.get(`${API}/application`, { headers: { "X-Admin-Token": tk } }),
      ]);
      setContacts(c.data);
      setApplications(a.data);
      setAuthed(true);
      localStorage.setItem("mag_admin_token", tk);
      setToken(tk);
    } catch (e) {
      setAuthed(false);
      setError("Falsches Passwort oder Serverfehler.");
      localStorage.removeItem("mag_admin_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const endpoint = tab === "contacts" ? "contact" : "application";
  const items = tab === "contacts" ? contacts : applications;
  const setItems = tab === "contacts" ? setContacts : setApplications;

  const updateStatus = async (id, status) => {
    try {
      await axios.post(`${API}/${endpoint}/${id}/status?status=${status}`, null, { headers: { "X-Admin-Token": token } });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    } catch (e) {
      alert("Aktion fehlgeschlagen.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/${endpoint}/${id}`, { headers: { "X-Admin-Token": token } });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      alert("Löschen fehlgeschlagen.");
    }
  };

  const logout = () => {
    localStorage.removeItem("mag_admin_token");
    setToken("");
    setAuthed(false);
    setContacts([]);
    setApplications([]);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-5">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600 text-white">
              <Building2 className="w-5 h-5" strokeWidth={2.4} />
            </div>
            <div className="font-display text-[18px] text-slate-900" style={{ fontWeight: 800 }}>MAG Admin</div>
          </div>
          <h1 className="text-[20px] font-bold text-slate-900">Verwaltung</h1>
          <p className="text-[14px] text-slate-500 mt-1">Bitte melden Sie sich an.</p>
          <div className="mt-6 relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(input)}
              placeholder="Passwort"
              className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-4 py-3 text-[15px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />
          </div>
          {error && <p className="text-[13px] text-red-600 mt-2">{error}</p>}
          <button onClick={() => load(input)} disabled={loading} className="mt-4 w-full py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60">
            {loading ? "Anmelden..." : "Anmelden"}
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: "contacts", label: "Kontaktanfragen", icon: Inbox, count: contacts.length },
    { key: "applications", label: "Bewerbungen", icon: Briefcase, count: applications.length },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <h1 className="font-display text-[22px]" style={{ fontWeight: 800 }}>MAG Verwaltung</h1>
          <div className="flex gap-2">
            <button onClick={() => load(token)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-[14px] font-medium transition-colors">
              <RefreshCw className="w-4 h-4" /> Aktualisieren
            </button>
            <button onClick={logout} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-[14px] font-medium transition-colors">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-[14px] font-semibold border-b-2 transition-colors ${
                tab === t.key ? "border-blue-500 text-white" : "border-transparent text-slate-400 hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              <span className="ml-1 text-[12px] px-2 py-0.5 rounded-full bg-white/10">{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        {items.length === 0 ? (
          <div className="text-center py-20 text-slate-500">Noch keine Einträge vorhanden.</div>
        ) : (
          <div className="space-y-4">
            {items.map((it) => (
              <div key={it.id} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-[17px] font-bold text-slate-900">{it.name}</h3>
                      <span className={`text-[12px] font-semibold px-2.5 py-0.5 rounded-full ${statusColors[it.status] || statusColors.neu}`}>{it.status}</span>
                      {(it.service || it.position) && (
                        <span className="text-[12px] font-medium px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{it.service || it.position}</span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[14px] text-slate-600">
                      <a href={`mailto:${it.email}`} className="flex items-center gap-1.5 hover:text-blue-600"><Mail className="w-4 h-4" /> {it.email}</a>
                      {it.phone && (<a href={`tel:${it.phone}`} className="flex items-center gap-1.5 hover:text-blue-600"><Phone className="w-4 h-4" /> {it.phone}</a>)}
                    </div>
                  </div>
                  <div className="text-[13px] text-slate-400">{fmtDate(it.created_at)}</div>
                </div>
                <p className="mt-4 text-[15px] text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-lg p-4">{it.message}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={() => updateStatus(it.id, "bearbeitet")} className="px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-[13px] font-semibold hover:bg-green-100 transition-colors">Als bearbeitet markieren</button>
                  <button onClick={() => updateStatus(it.id, "archiviert")} className="px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600 text-[13px] font-semibold hover:bg-slate-200 transition-colors">Archivieren</button>
                  <button onClick={() => remove(it.id)} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-[13px] font-semibold hover:bg-red-100 transition-colors"><Trash2 className="w-4 h-4" /> Löschen</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
